import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDownload, FiCopy, FiCheck, FiLoader } = FiIcons;

function TextOutput({ content, isProcessing }) {
  const [copied, setCopied] = useState(false);
  const linkRef = useRef(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleDownload = () => {
    if (!content) {
      console.error('No content to download');
      return;
    }

    // Define filename here to be accessible in both try and catch blocks
    const timestamp = new Date().toISOString().split('T')[0];
    const timeString = new Date().toTimeString().split(' ')[0].replace(/:/g, '-');
    const filename = `code-analysis-${timestamp}-${timeString}.txt`;

    try {
      // Create blob with proper MIME type
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      
      // Create object URL
      const url = URL.createObjectURL(blob);
      
      // Use the ref-based approach
      if (linkRef.current) {
        linkRef.current.href = url;
        linkRef.current.download = filename;
        linkRef.current.click();
        
        // Cleanup after a short delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
          linkRef.current.href = '';
        }, 100);
      } else {
        // Fallback to creating element dynamically
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
      }
      
    } catch (err) {
      console.error('Failed to download file:', err);
      
      // Ultimate fallback - open in new window
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Code Analysis - ${filename}</title>
            <style>
              body { font-family: monospace; padding: 20px; white-space: pre-wrap; }
            </style>
          </head>
          <body>${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</body>
          </html>
        `);
        newWindow.document.close();
        newWindow.focus();
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full">
      {/* Hidden download link for better reliability */}
      <a
        ref={linkRef}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Text Output</h3>
          
          {content && (
            <div className="flex items-center space-x-2">
              <motion.button
                onClick={handleCopy}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="text-sm" />
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </motion.button>
              
              <motion.button
                onClick={handleDownload}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiDownload} className="text-sm" />
                <span>Download</span>
              </motion.button>
            </div>
          )}
        </div>
      </div>
      
      <div className="p-4">
        {isProcessing ? (
          <div className="flex items-center justify-center py-12">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="mr-3"
            >
              <SafeIcon icon={FiLoader} className="text-2xl text-blue-600" />
            </motion.div>
            <p className="text-slate-600">Generating text output...</p>
          </div>
        ) : content ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600 mb-2">
                Generated {content.split('\n').length} lines • {content.length} characters
              </p>
              <div className="bg-white rounded border border-slate-200 p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-slate-800 whitespace-pre-wrap font-mono">
                  {content}
                </pre>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <SafeIcon icon={FiDownload} className="text-4xl text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              Select files and click "Generate Text" to see the output here
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TextOutput;